import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import MasterPage from './layouts/MasterPage';
import Home from './pages/Common/Home';
import About from './pages/Common/About';
import ScrubProcess from './pages/ScrubProcess/ScrubProcess';
import NegotiatorWorklist from './pages/NegoWorklist/NegotiatorWorklist';
import DebtSettlementCalculator from './pages/Calculator_DebtSettlement/DebtSettlementCalculator';
import EPFRebalancer from './pages/Calculator_EpfRebalancer/EPFRebalancer';
import SIFOfferSimulator from './pages/Calculator_SIFOfferDraft/SIFOfferSimulator';
import ProductionPulse from './pages/ProductionPulse/ProductionPulse';
import BulkUpdateTool from './pages/BulkUpdateTool/BulkUpdateTool';
import Authentication from './pages/UserSetup/Authentication';
import UserManagement from './pages/UserSetup/UserManagement';
import loadingGif from './images/Loading_2.gif';
import PaymentChangeCalculator from './pages/Calculator_PaymentChange/PaymentChangeCalculator';
import PaymentChangeHistory from './pages/Calculator_PaymentChange/PaymentChangeHistory';
import SettlementVault from './pages/SettlementVault/SettlementVault';
import EPFRebalancer_Test from './pages/Calculator_EpfRebalancer/EPFRebalancer_Test';
import EPFRebalancer_Secured_Token_Test from './pages/Calculator_EpfRebalancer/EPFRebalancer_Secured_Token_Test';
import ModuleManagement from './pages/ModuleManagement/ModuleManagement';
import SettlementProcessingHub from './pages/SettlementProcessingHub/SettlementProcessingHub';
import BulkTaskCreator from './pages/BulkTaskCreator/BulkTaskCreator';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuthorization = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          if (requiredRole && parsedUser.role) {
            setIsAuthorized(parsedUser.role.toLowerCase() === requiredRole.toLowerCase());
          } else {
            setIsAuthorized(false);
          }
        } catch (error) {
          console.error('Error checking authorization:', error);
          setIsAuthorized(false);
        }
      } else {
        setIsAuthorized(false);
      }
      setIsChecking(false);
    };

    checkAuthorization();
  }, [requiredRole]);

  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '20px'
        }}>
          <img 
            src={loadingGif} 
            alt="Loading..." 
            style={{
              width: '50px',
              height: '50px',
              marginBottom: '10px'
            }}
          />
          <p>Checking authorization...</p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #856404',
          borderRadius: '8px',
          padding: '30px',
          maxWidth: '500px',
          textAlign: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ color: '#856404', marginBottom: '20px' }}>Access Denied</h2>
          <p style={{ color: '#856404', marginBottom: '20px' }}>
            You don't have permission to access this page. This area is restricted to Super Admin users only.
          </p>
          <button
            onClick={() => window.location.href = '/pdr-coreapp3/dashboard'}
            style={{
              backgroundColor: '#0066CC',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0052a3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#0066CC'}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return children;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          if (parsedUser && parsedUser.userId && parsedUser.email) {
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('user');
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          localStorage.removeItem('user');
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0066CC 0%, #8BC34A 100%)'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
        }}>
          <img 
            src={loadingGif} 
            alt="Loading..." 
            style={{
              width: '80px',
              height: '80px',
              marginBottom: '20px'
            }}
          />
          <p style={{
            color: '#545454',
            fontSize: '16px',
            fontWeight: '500',
            margin: '0'
          }}>Loading PDR System...</p>
        </div>
      </div>
    );
  }

  return (
    <Router basename="/pdr-coreapp3">
      {!isAuthenticated ? (
        <Routes>
          <Route path="/auth" element={<Authentication />} />
          <Route path="*" element={<Navigate to="/auth" replace />} />
        </Routes>
      ) : (
        <MasterPage>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/dashboard" element={<Home />} />

            <Route path="/Scrub-Process" element={<ScrubProcess />} />
            <Route path="/Bulk-Update-Tool" element={<BulkUpdateTool />} />
            <Route path="/Negotiator-Worklist" element={<NegotiatorWorklist />} />
            <Route path="/Debt-Settlement-Calculator" element={<DebtSettlementCalculator />} />
            <Route path="/EPF-Rebalancer" element={<EPFRebalancer />} />
            <Route path="/EPF-Rebalancer_Test" element={<EPFRebalancer_Test />} />
            <Route path="/EPF-Rebalancer_Secured_Token_Test" element={<EPFRebalancer_Secured_Token_Test />} />
            <Route path="/SIF-Offer-Simulator" element={<SIFOfferSimulator />} />
            <Route path="/Product-Pulse" element={<ProductionPulse />} />
            <Route path="/Settlement-Processing-Hub" element={<SettlementProcessingHub />} />
            <Route path="/Bulk-Task-Creator" element={<BulkTaskCreator />} />
            <Route path="/Settlement-Vault" element={<SettlementVault />} />
            <Route path="/Payment-Change-Calculator" element={<PaymentChangeCalculator />} />
            <Route path="/Payment-Change-History" element={<PaymentChangeHistory />} />
            <Route 
              path="/user-management" 
              element={
                <ProtectedRoute requiredRole="super admin">
                  <UserManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/module-management" 
              element={
                <ProtectedRoute requiredRole="super admin">
                  <ModuleManagement />
                </ProtectedRoute>
              } 
            />
            <Route path="/auth" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </MasterPage>
      )}
    </Router>
  );
};

export default App;